import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Play } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PublicShell, Loading } from "@/components/shared";
import { NewsletterInline, ExitIntentModal } from "@/components/Newsletter";
import { ShareButtons } from "@/components/ShareButtons";
import api from "@/lib/api";

function youtubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["blog", slug], queryFn: async () => (await api.get(`/blog/${slug}`)).data });

  useEffect(() => {
    if (data?.archived) navigate("/blog", { replace: true });
  }, [data, navigate]);

  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  if (!data || data.archived) return <PublicShell><Loading label="Redirecting…" /></PublicShell>;

  const yt = youtubeEmbed(data.video_url);
  const region = data.region, sector = data.sector, company = data.company;

  return (
    <PublicShell>
      <ExitIntentModal />
      <article className="mx-auto max-w-3xl px-5 py-12 lg:px-8" data-testid="blog-post">
        <button onClick={() => navigate("/blog")} className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-muted hover:text-ink" data-testid="blog-back"><ArrowLeft className="h-4 w-4" /> Back to blog</button>
        <div className="mb-3 flex flex-wrap gap-1.5">{data.tags?.map((t) => <span key={t} className="rounded bg-card px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">{t}</span>)}</div>
        {data.pillar && <p className="kicker mb-3 text-xs text-accent" data-testid="blog-pillar">{data.pillar}</p>}
        <h1 className="font-heading text-[clamp(2rem,5vw,2.75rem)] font-extrabold leading-tight text-ink">{data.title}</h1>
        {data.dek && <p className="mt-3 text-lg leading-relaxed text-muted">{data.dek}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
          <span>By {data.author}</span>
          {company && <><span>·</span><span>{company}</span></>}
          {region && <><span>·</span><span>{region}</span></>}
          {sector && <><span>·</span><span>{sector}</span></>}
        </div>

        {(yt || data.video_url) && (
          <div className="mt-6" data-testid="blog-video">
            {yt ? (
              <div className="aspect-video w-full overflow-hidden rounded-xl border border-border">
                <iframe src={yt} title={data.title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <a href={data.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-3 text-sm font-semibold text-white hover:opacity-90"><Play className="h-4 w-4" /> Watch the video</a>
            )}
          </div>
        )}
        {data.cover_image && <img src={data.cover_image} alt={data.title} className="mt-6 aspect-[16/9] w-full rounded-xl border border-border object-cover" />}

        <div className="prose mt-8 max-w-none prose-headings:font-heading prose-headings:text-ink prose-h2:text-2xl prose-h2:mt-10 prose-p:text-ink prose-p:leading-[1.75] prose-a:text-accent prose-strong:text-ink prose-blockquote:border-l-accent prose-blockquote:text-ink prose-blockquote:font-medium prose-blockquote:not-italic prose-li:text-ink" data-testid="blog-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content}</ReactMarkdown>
        </div>

        <div className="mt-10 border-t border-border pt-6" data-testid="blog-share-block">
          <p className="mb-3 text-sm font-semibold text-muted">Know someone who should read this?</p>
          <ShareButtons url={`${window.location.origin}/blog/${data.slug}`} title={data.title} testid="blog-share" />
        </div>

        {Array.isArray(data.sources) && data.sources.length > 0 && (
          <div className="mt-12 border-t border-border pt-6" data-testid="blog-sources">
            <h3 className="kicker text-sm font-bold text-muted">Sources</h3>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm">
              {data.sources.map((s, i) => (
                <li key={i}><a href={s.url} target="_blank" rel="noreferrer" className="text-accent hover:underline" data-testid={`source-link-${i}`}>{s.title}</a></li>
              ))}
            </ol>
          </div>
        )}

        <div className="mt-10 border-t border-border pt-6 text-[15px] text-ink" data-testid="blog-apply-cta">
          Run a business doing ₹1–5 crore and want it to stop depending on you?{" "}
          <button onClick={() => navigate("/apply")} className="font-semibold text-accent underline-offset-4 hover:underline">The Room is where I teach that.</button>
        </div>

        <div className="mt-10" data-testid="blog-post-footer-signup">
          <NewsletterInline source="post-footer" />
        </div>
      </article>
    </PublicShell>
  );
}
